import { useEffect, useState } from "react";
import { Button } from "@components/ui/button";
import { Badge } from "@components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@components/ui/sheet";
import { Skeleton } from "@components/ui/skeleton";
import { getEntityRoutingRationale } from "@/lib/auth";
import type { RoutingRationaleEntry } from "@io-spoke/contracts/entities";

type RoutingRationaleSheetProps = {
  open: boolean;
  entityId: string | null;
  entityTitle?: string;
  threadId?: string;
  onClose: () => void;
  navigate: (route: string) => void;
};

function formatDecisionType(decisionType: RoutingRationaleEntry["routingRationale"]["decisionType"]) {
  if (decisionType === "auto") {
    return "Auto assignment";
  }
  if (decisionType === "override") {
    return "Override";
  }
  return "Manual assignment";
}

function humanizeFlag(flag: string) {
  return flag
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
}

function describeFlag(flag: string) {
  if (flag === "slaBreached") {
    return "The route was captured after the SLA deadline had already been missed.";
  }
  if (flag === "slaBreachRisk") {
    return "The item was close to breaching SLA when this routing decision was made.";
  }
  if (flag === "lowConfidence") {
    return "Model confidence was below the current routing guardrail.";
  }
  if (flag === "fallbackUsed") {
    return "Fallback heuristics were used instead of the primary routing path.";
  }
  if (flag === "requiresOverride") {
    return "The item still required reviewer intervention before the route could be trusted.";
  }
  if (flag === "manualOverride") {
    return "A human changed the original route after reviewing the work item.";
  }
  return "This signal was captured as part of the routing guardrail set.";
}

function formatSlaState(state: RoutingRationaleEntry["routingRationale"]["slaState"]) {
  if (state.status === "none") {
    return "No SLA";
  }
  if (state.status === "breached") {
    return "SLA breached";
  }
  if (state.minutesRemaining !== undefined) {
    if (state.minutesRemaining <= 60) {
      return `SLA at risk (${state.minutesRemaining}m left)`;
    }
    return `SLA on track (${state.minutesRemaining}m left)`;
  }
  return "SLA at risk";
}

function compareAssignment(
  current: RoutingRationaleEntry,
  previous?: RoutingRationaleEntry
) {
  if (!previous) {
    return undefined;
  }
  const currentRoute = `${current.routingRationale.assigneeType} ${current.routingRationale.assigneeId}`;
  const previousRoute = `${previous.routingRationale.assigneeType} ${previous.routingRationale.assigneeId}`;
  if (currentRoute === previousRoute) {
    return "Assignment stayed the same.";
  }
  return `Changed from ${previousRoute} to ${currentRoute}.`;
}

export function RoutingRationaleSheet({
  open,
  entityId,
  entityTitle,
  threadId,
  onClose,
  navigate,
}: RoutingRationaleSheetProps) {
  const [entries, setEntries] = useState<RoutingRationaleEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !entityId) {
      setEntries([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    let active = true;
    setIsLoading(true);
    setError(null);
    getEntityRoutingRationale(entityId)
      .then((payload) => {
        if (!active) {
          return;
        }
        setEntries(payload.rationales);
      })
      .catch((err) => {
        if (!active) {
          return;
        }
        setError(
          err instanceof Error ? err.message : "Unable to load routing rationale."
        );
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [entityId, open]);

  const handleViewConversation = () => {
    if (threadId) {
      navigate(`/threads/${threadId}`);
      onClose();
    }
  };

  const latestEntry = entries[0];
  const previousEntry = entries.length > 1 ? entries[1] : undefined;
  const latestWarnings =
    latestEntry?.routingRationale.guardrailFlags.filter((flag) =>
      ["slaBreached", "slaBreachRisk", "lowConfidence", "fallbackUsed"].includes(flag)
    ) ?? [];
  const showOverrideComparison =
    latestEntry?.routingRationale.decisionType === "override" && previousEntry;

  return (
    <Sheet open={open} onOpenChange={(value) => !value && onClose()}>
      <SheetContent side="right" className="max-w-md">
        <SheetHeader>
          <SheetTitle>Routing rationale</SheetTitle>
          <SheetDescription>
            {entityTitle ? `${entityTitle} ·` : ""} recent guardrails,
            intent, and confidence signals for this assignment.
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 py-4">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-6 w-full" />
              ))}
            </div>
          ) : error ? (
            <p className="text-sm text-rose-400" role="alert">
              {error}
            </p>
          ) : entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No routing rationale captured yet. Assignments will log the signal
              set once the routing decision runs.
            </p>
          ) : (
            <>
              {latestEntry ? (
                <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
                  <div className="min-w-0 flex-1 basis-48 space-y-1 break-words">
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">
                      Current decision
                    </p>
                    <p className="text-sm font-semibold">
                      {formatDecisionType(latestEntry.routingRationale.decisionType)} ·{" "}
                      {latestEntry.routingRationale.assigneeType}{" "}
                      {latestEntry.routingRationale.assigneeId}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Assigned {new Date(latestEntry.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <p className="text-sm text-foreground/90">{latestEntry.summary}</p>
                  <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                    {latestEntry.routingRationale.intent ? (
                      <span>Intent: {latestEntry.routingRationale.intent}</span>
                    ) : null}
                    {typeof latestEntry.routingRationale.confidence === "number" ? (
                      <span>
                        Confidence: {latestEntry.routingRationale.confidence.toFixed(2)}
                      </span>
                    ) : null}
                    <span>
                      SLA: {formatSlaState(latestEntry.routingRationale.slaState)}
                    </span>
                    {latestEntry.routingRationale.requestId ? (
                      <span>Request ID: {latestEntry.routingRationale.requestId}</span>
                    ) : null}
                    <span>
                      Diagnostics:{" "}
                      {latestEntry.routingRationale.fallbackUsed ? "Fallback" : "Primary"}
                      {" · "}
                      {latestEntry.routingRationale.cacheHit ? "Cache hit" : "Cache miss"}
                    </span>
                  </div>
                  {latestWarnings.length ? (
                    <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 p-3">
                      <p className="text-xs uppercase tracking-[0.2em] text-amber-900 dark:text-amber-200">
                        Guardrail warnings
                      </p>
                      {latestWarnings.map((flag) => (
                        <div key={flag} className="space-y-1">
                          <Badge variant="outline">
                            {humanizeFlag(flag)}
                          </Badge>
                          <p className="text-xs text-amber-900 dark:text-amber-200">
                            {describeFlag(flag)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {showOverrideComparison ? (
                    <div className="space-y-2 rounded-md border border-border/70 bg-background/40 p-3">
                      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">
                        Override comparison
                      </p>
                      <p className="text-sm text-foreground/90">
                        {compareAssignment(latestEntry, previousEntry)}
                      </p>
                      {latestEntry.routingRationale.overrideReason ? (
                        <p className="text-xs text-muted-foreground">
                          Human rationale: {latestEntry.routingRationale.overrideReason}
                        </p>
                      ) : null}
                      <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                        <span>
                          Before: {formatDecisionType(previousEntry.routingRationale.decisionType)}
                        </span>
                        <span>
                          Before SLA: {formatSlaState(previousEntry.routingRationale.slaState)}
                        </span>
                        {previousEntry.routingRationale.intent ? (
                          <span>Before intent: {previousEntry.routingRationale.intent}</span>
                        ) : null}
                        {typeof previousEntry.routingRationale.confidence === "number" ? (
                          <span>
                            Before confidence:{" "}
                            {previousEntry.routingRationale.confidence.toFixed(2)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">
                  Recent versions
                </p>
                {entries.map((entry) => {
                  const { routingRationale } = entry;
                  return (
                    <div
                      key={entry.eventId}
                      className="space-y-2 rounded-md border border-border bg-muted/30 p-3"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 basis-48 space-y-1 break-words">
                          <p className="text-sm font-semibold">
                            {formatDecisionType(routingRationale.decisionType)} ·{" "}
                            {routingRationale.assigneeType} {routingRationale.assigneeId}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Assigned {new Date(entry.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <Badge variant="outline">
                          {routingRationale.assigneeId}
                        </Badge>
                      </div>
                      <p className="text-sm text-foreground/90">{entry.summary}</p>
                      {routingRationale.guardrailFlags.length ? (
                        <div className="flex flex-wrap gap-2">
                          {routingRationale.guardrailFlags.map((flag) => (
                            <Badge key={flag} variant="outline">
                              {humanizeFlag(flag)}
                            </Badge>
                          ))}
                        </div>
                      ) : null}
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span>
                          Actor: {entry.actorType} {entry.actorId}
                        </span>
                        {routingRationale.load?.teamOccupancy !== undefined ? (
                          <span>
                            Team occupancy: {routingRationale.load.teamOccupancy}%
                          </span>
                        ) : null}
                        {routingRationale.load?.agentOccupancy !== undefined ? (
                          <span>
                            Agent occupancy: {routingRationale.load.agentOccupancy}%
                          </span>
                        ) : null}
                        {routingRationale.load?.fallbackQueueDepth !== undefined ? (
                          <span>
                            Fallback depth: {routingRationale.load.fallbackQueueDepth}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <SheetFooter className="justify-between">
          {threadId ? (
            <Button variant="ghost" onClick={handleViewConversation}>
              Open conversation
            </Button>
          ) : (
            <div />
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
